import Card from "../../components/base/Card";
import { useInterview } from "./useInterview";

interface Props {
    onComplete: () => void;
}

export default function InterviewScreen({ onComplete }: Props) {
    const { isAISpeaking, isListening, isLoading, handleVoiceInput } = useInterview(onComplete);

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="text-center">
                    <Card className="max-w-2xl mx-auto" padding="md">
                        {/* Status Display */}
                        <div className="mb-8">
                            {isLoading && (
                                <div className="text-center">
                                    <div className="inline-flex items-center space-x-2 text-gray-600">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                                        <span className="text-lg">Connecting to AI interviewer...</span>
                                    </div>
                                </div>
                            )}

                            {isAISpeaking && !isLoading && (
                                <div className="text-center">
                                    <h2 className="text-2xl font-bold text-purple-900 mb-2">AI is speaking...</h2>
                                    <p className="text-gray-600">Listen carefully to the question</p>
                                </div>
                            )}

                            {isListening && !isLoading && (
                                <div className="text-center">
                                    <h2 className="text-2xl font-bold text-green-900 mb-2">Your turn to speak</h2>
                                    <p className="text-gray-600">Share your thoughts and experiences</p>
                                </div>
                            )}

                            {!isAISpeaking && !isListening && !isLoading && (
                                <div className="text-center">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready for next question</h2>
                                    <p className="text-gray-600">Tap the microphone to continue</p>
                                </div>
                            )}
                        </div>

                        {/* Voice Control Button */}
                        <div className="flex justify-center">
                            <button
                                onClick={handleVoiceInput}
                                disabled={isAISpeaking || isLoading}
                                className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl transition-all duration-200 
                  ${isListening
                                        ? "bg-red-500 hover:bg-red-600 text-white animate-pulse"
                                        : isAISpeaking || isLoading
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            : "bg-green-500 hover:bg-green-600 text-white hover:scale-110"
                                    }`}
                            >
                                <i className={isListening ? "ri-stop-circle-line" : "ri-mic-line"}></i>
                            </button>
                        </div>

                        {/* Instructions */}
                        <div className="mt-8 text-center">
                            <p className="text-sm text-gray-500">
                                {isListening
                                    ? "Tap the red button to stop recording"
                                    : isAISpeaking || isLoading
                                        ? "Please wait..."
                                        : "Tap the green button to start speaking"}
                            </p>
                        </div>
                    </Card>

                    {/* Quick Tips */}
                    <Card padding="lg" className="mt-8 bg-blue-50 border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3">
                            <i className="ri-lightbulb-line mr-2"></i>
                            Voice Interview Tips
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="text-sm text-blue-800 bg-white rounded-lg p-3">
                                <i className="ri-volume-up-line mr-2 text-blue-600"></i>
                                Speak clearly and at normal pace
                            </div>
                            <div className="text-sm text-blue-800 bg-white rounded-lg p-3">
                                <i className="ri-time-line mr-2 text-blue-600"></i>
                                Take your time to think before answering
                            </div>
                            <div className="text-sm text-blue-800 bg-white rounded-lg p-3">
                                <i className="ri-check-line mr-2 text-blue-600"></i>
                                Use specific examples from your experience
                            </div>
                            <div className="text-sm text-blue-800 bg-white rounded-lg p-3">
                                <i className="ri-heart-line mr-2 text-blue-600"></i>
                                Show enthusiasm and passion
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
